import React, { Dispatch, RefObject, SetStateAction, useContext } from "react";
import { ChevronDown, Moon, Sun } from "lucide-react";
import { AppTheme, THEMES } from "../../core/theme/theme.config";
import { getFullName } from "../../utility/utility";
import { DropdownItem, getDropdownItems, ROLE_STYLE } from "./config/user-dropdown.config";
import UserLoginContext from "../../context/userLoginContext";
import { UserRole } from "../../core/roles/roles.type";


interface UserDropdownViewProps {
    role: UserRole;
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    activeTheme: AppTheme;
    containerRef: RefObject<HTMLDivElement|null>;
    handleItemClick: (item: DropdownItem) => void;
    onThemeChange: (theme: AppTheme) => void;
    handleModeToggle: () => void;
    isLight: boolean;
}

function UserDropdownView(props: UserDropdownViewProps) {
    const {user} = useContext(UserLoginContext);

    const roleStyle = ROLE_STYLE[props.role] ?? ROLE_STYLE.EMPLOYEE;

    const items = getDropdownItems(props.role);

    return (
        <div ref={props.containerRef} className="pn-dropdown">
            <button
                className={`pn-dropdown__trigger${props.open ? " pn-dropdown__trigger--open" : ""}`}
                onClick={() => props.setOpen((prev) => !prev)}
                aria-haspopup="dialog"
                aria-expanded={props.open}
                aria-label="Open user menu"
            >
                <img
                    src={user?.profileURL || "https://api.dicebear.com/7.x/miniavs/svg?seed=8"}
                    alt={"user-image"}
                    className="pn-header__avatar"
                />
                <div className="pn-header__user-info">
                    <span className="pn-header__user-name">{getFullName(user.name)}</span>
                    <span
                        className={`pn-header__role-badge ${props?.role?.toLowerCase() || ""}`}
                    >
                        {props.role}
                    </span>
                </div>
                <ChevronDown
                    size={14}
                    className={`pn-dropdown__trigger-chevron${props.open ? " pn-dropdown__trigger-chevron--rotated" : ""}`}
                />
            </button>

            {props.open && (
                <div className="pn-dropdown__panel" role="dialog" aria-label="User menu">
                    <div className="pn-dropdown__identity">
                        <img
                            src={user?.profileURL || "https://api.dicebear.com/7.x/miniavs/svg?seed=8"}
                            alt={"user-image"}
                            className="pn-dropdown__identity-avatar"
                        />
                        <div className="pn-dropdown__identity-info">
                            <span className="pn-dropdown__identity-name">{getFullName(user.name)}</span>
                            <span className="pn-dropdown__identity-email">{user.email}</span>
                            <div className="pn-dropdown__identity-roles">
                                {
                                    user.roles.map((role: UserRole) => {
                                        return (
                                            <span
                                                key={role.toLocaleLowerCase()}
                                                className={`pn-dropdown__identity-role ${role.toLocaleLowerCase()}`}
                                            >
                                            {role}
                                        </span>
                                        )
                                    })
                                }
                            </div>
                        </div>
                    </div>

                    <div className="pn-dropdown__mode">
                        <span className="pn-dropdown__mode-label">
                            {props.isLight ? <Sun size={14}/> : <Moon size={14}/>}
                            {props.isLight ? "Light mode" : "Dark mode"}
                        </span>
                        <button
                            className={`pn-dropdown__mode-toggle${props.isLight ? " pn-dropdown__mode-toggle--light" : ""}`}
                            onClick={props.handleModeToggle}
                            aria-pressed={props.isLight}
                            aria-label={props.isLight ? "Switch to dark mode" : "Switch to light mode"}
                        />
                    </div>

                    <div className="pn-dropdown__theme">
                        <span className="pn-dropdown__theme-label">Accent colour</span>
                        <div className="pn-dropdown__theme-swatches" role="radiogroup"
                             aria-label="Choose accent colour">
                            {THEMES.map(theme => (
                                <button
                                    key={theme.id}
                                    className={`pn-dropdown__theme-swatch${props.activeTheme.id === theme.id ? " pn-dropdown__theme-swatch--active" : ""}`}
                                    style={{background: theme.swatch}}
                                    title={theme.label}
                                    onClick={() => props.onThemeChange(theme)}
                                    role="radio"
                                    aria-checked={props.activeTheme.id === theme.id}
                                    aria-label={`${theme.label} theme`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="pn-dropdown__list">
                        {items.map((item: DropdownItem) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.id}
                                    className={[
                                        "pn-dropdown__item",
                                        item.id === "logout" ? "pn-dropdown__item--logout" : "",
                                        item.groupEnd ? "pn-dropdown__item--group-end" : "",
                                        item.disabled ? "pn-dropdown__item--disabled" : "",
                                    ].filter(Boolean).join(" ")}
                                    onClick={() => props.handleItemClick(item)}
                                    aria-disabled={item.disabled}
                                >
                                    <span className="pn-dropdown__item-icon"><Icon size={15} strokeWidth={1.75}/></span>
                                    <span className="pn-dropdown__item-label">{item.label}</span>
                                    {item.badge && <span className="pn-dropdown__item-badge">{item.badge}</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserDropdownView;
